// InviteRedeemPage — public landing for /wars/invite/:token
//
// Two flows:
//  1. Anon viewer → "Sign up & accept" persists token to localStorage,
//     bounces to /signup. Once authed, /wars-page redeem hook fires
//     automatically (see useEffect in BattlesHub).
//  2. Already-authed viewer → button calls redeem directly.
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Sword, ArrowRight, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { getUser } from "@/lib/api";
import { toast } from "sonner";

const PENDING_TOKEN_KEY = "battle_pending_invite_token";

export default function InviteRedeemPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const user = getUser();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/battles/invite/${token}`);
        setInvite(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Invite not found.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const stashAndSignup = () => {
    try { localStorage.setItem(PENDING_TOKEN_KEY, token); } catch { /* ignore */ }
    nav("/signup");
  };

  const redeemNow = async () => {
    setRedeeming(true);
    try {
      await api.post(`/battles/invite/${token}/redeem`, {});
      try { localStorage.removeItem(PENDING_TOKEN_KEY); } catch { /* ignore */ }
      toast.success("Invite redeemed. Build your silo to accept the battle.");
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redeem.");
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#F5F2ED] flex items-center justify-center text-[#6B6A68]">Loading…</div>;

  if (error) {
    return (
      <div className="min-h-screen bg-[#F5F2ED] flex items-center justify-center px-6">
        <div className="bg-white border border-[#D8D6D2] rounded-lg p-8 max-w-md text-center">
          <p className="text-[#C16E5A] mb-2 text-sm uppercase tracking-widest">Invite unavailable</p>
          <p className="text-[#1C1B1A] mb-4">{error}</p>
          <Link to="/" className="text-sm text-[#1C1B1A] underline">Back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F2ED]" data-testid="invite-redeem-page">
      <header className="border-b border-[#D8D6D2] bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-base font-serif tracking-tight text-[#1C1B1A]">My Silo</Link>
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#A89360]">
            <Sword size={14} className="text-[#C16E5A]" />
            Silo Wars
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-16">
        <div className="bg-white border border-[#D8D6D2] rounded-xl p-10">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#C16E5A] mb-2">● Battle invitation</p>
          <h1 className="font-serif-display text-4xl text-[#1C1B1A] leading-tight mb-4">
            {invite.inviter_name} wants to fight your AI.
          </h1>
          <p className="text-[#1C1B1A] text-base leading-relaxed mb-6">
            Their silo <strong>{invite.inviter_silo_name}</strong>
            {invite.inviter_silo_tagline && <span className="text-[#6B6A68]"> — {invite.inviter_silo_tagline}</span>}
            {' '}— just threw down a challenge. But you don't have a silo yet. Build one and answer back.
          </p>

          {invite.topic && (
            <div className="mb-4 p-4 bg-[#FAFAFA] border border-[#EFEDE8] rounded-md">
              <div className="text-[10px] uppercase tracking-widest text-[#6B6A68] mb-1">Topic</div>
              <div className="text-[#1C1B1A]">{invite.topic}</div>
            </div>
          )}
          {invite.note && (
            <blockquote className="my-4 pl-4 border-l-4 border-[#A89360] text-[#1C1B1A] italic" data-testid="invite-note">
              "{invite.note}"
            </blockquote>
          )}
          {invite.is_broadcast === false && (
            <p className="text-xs text-[#A89360] flex items-center gap-1 mb-4"><Lock size={12} /> This will be a private match.</p>
          )}

          {invite.status !== "pending" && invite.status !== "signed_up" && (
            <p className="text-[#6B6A68] mt-6">This invite has already been redeemed.</p>
          )}

          {(invite.status === "pending" || invite.status === "signed_up") && (
            <>
              <div className="border-t border-[#EFEDE8] my-6 pt-6 space-y-3">
                <div className="text-sm font-semibold text-[#1C1B1A]">What's a silo?</div>
                <p className="text-sm text-[#6B6A68] leading-relaxed">
                  An AI version of you, trained on your own writing or expertise. Battles run round-by-round, an LLM referee scores each answer, and an Elo rating tracks who's on a hot streak. It takes a few minutes to set one up.
                </p>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                {user ? (
                  <button
                    onClick={redeemNow}
                    disabled={redeeming}
                    className="inline-flex items-center gap-2 px-5 py-3 bg-[#1C1B1A] text-white rounded-md hover:bg-[#2C2B2A] disabled:opacity-60"
                    data-testid="invite-redeem-btn"
                  >
                    {redeeming ? "Redeeming…" : "Accept the challenge"} <ArrowRight size={14} />
                  </button>
                ) : (
                  <button
                    onClick={stashAndSignup}
                    className="inline-flex items-center gap-2 px-5 py-3 bg-[#1C1B1A] text-white rounded-md hover:bg-[#2C2B2A]"
                    data-testid="invite-signup-btn"
                  >
                    Build your silo & accept <ArrowRight size={14} />
                  </button>
                )}
                <Link to="/login" className="text-sm text-[#6B6A68] hover:text-[#1C1B1A] underline">
                  Already have an account? Sign in.
                </Link>
              </div>
              <p className="text-[11px] text-[#6B6A68] mt-6 leading-relaxed">
                Invite sent to <strong>{invite.invitee_email}</strong>. If this doesn't sound like your thing, no harm — just ignore the email.
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
